/**
 * Results Page Component
 * Displays simulation results with search, filters, selection, tags, and notes functionality
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Download, Eye, StickyNote, ArrowUpDown, X, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import axios from 'axios';
import { AppHeader } from './AppHeader';
import { API_BASE, getAuthHeaders } from '../../utils/api';
import { FinalEvaluation } from '../../v4/components/FinalEvaluation';

interface Candidate {
    id: string;
    name: string;
    email: string;
    status: string;
    sessions_completed: number;
    average_score: number | null;
    total_time_minutes: number;
    last_active: string | null;
    created_at: string;
    image_url?: string;
    simulation_title?: string;
    position_applied?: string;
    session_id?: string;
    user_id?: string;
}

interface CandidateTag {
    id: string;
    candidate_id: string;
    session_id?: string;
    external_id?: string;
    tag: string;
    color: string;
}

interface CandidateNote {
    id: string;
    candidate_id: string;
    session_id?: string;
    external_id?: string;
    content: string;
    created_by: string;
    created_at: string;
}

type SortField = 'name' | 'score' | 'date' | 'status';
type SortDirection = 'asc' | 'desc';

interface ResultsPageProps {
    // Props are now optional since navigation is handled by router
}

const PREDEFINED_TAGS = [
    'Strong hire',
    'Move to final round',
    'Needs follow-up',
    'Consider for role',
    'Top performer',
    'Requires review'
];

const TAG_COLORS: Record<string, string> = {
    'Strong hire': 'bg-green-100 text-green-700',
    'Move to final round': 'bg-blue-100 text-blue-700',
    'Needs follow-up': 'bg-yellow-100 text-yellow-700',
    'Consider for role': 'bg-purple-100 text-purple-700',
    'Top performer': 'bg-emerald-100 text-emerald-700',
    'Requires review': 'bg-orange-100 text-orange-700',
    'default': 'bg-slate-100 text-slate-700'
};

function TableRowSkeleton() {
    return (
        <tr className="border-b border-slate-100">
            <td className="p-4"><div className="w-4 h-4 bg-slate-200 rounded animate-pulse"></div></td>
            <td className="px-6 py-4">
                <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-slate-200 rounded-full animate-pulse"></div>
                    <div className="h-4 w-28 bg-slate-200 rounded animate-pulse"></div>
                </div>
            </td>
            <td className="px-6 py-4"><div className="h-4 w-32 bg-slate-200 rounded animate-pulse"></div></td>
            <td className="px-6 py-4"><div className="h-4 w-12 bg-slate-200 rounded animate-pulse text-center"></div></td>
            <td className="px-6 py-4"><div className="h-6 w-20 bg-slate-200 rounded-full animate-pulse"></div></td>
            <td className="px-6 py-4"><div className="h-4 w-24 bg-slate-200 rounded animate-pulse"></div></td>
            <td className="px-6 py-4"><div className="h-4 w-32 bg-slate-200 rounded animate-pulse"></div></td>
            <td className="px-6 py-4"><div className="h-8 w-8 bg-slate-200 rounded animate-pulse"></div></td>
        </tr>
    );
}

export function ResultsPage({}: ResultsPageProps) {
    const navigate = useNavigate();
    const [candidates, setCandidates] = useState<Candidate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [sortField, setSortField] = useState<SortField>('date');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
    const [exporting, setExporting] = useState(false);

    // Tags and Notes state
    const [candidateTags, setCandidateTags] = useState<Record<string, string[]>>({});
    const [candidateNotes, setCandidateNotes] = useState<Record<string, CandidateNote[]>>({});
    const [showTagMenu, setShowTagMenu] = useState<string | null>(null);
    const [tagMenuPosition, setTagMenuPosition] = useState<{ top: number; left: number } | null>(null);
    const [hoveredCellCandidate, setHoveredCellCandidate] = useState<string | null>(null);
    const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
    const tooltipHideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [showAnnotationModal, setShowAnnotationModal] = useState(false);
    const [annotatingCandidate, setAnnotatingCandidate] = useState<Candidate | null>(null);
    const [annotationText, setAnnotationText] = useState('');


    useEffect(() => {
        loadCandidates();
        loadTagsAndNotes();
    }, []);

    const loadCandidates = async () => {
        try {
            const authHeaders = await getAuthHeaders();
            const response = await axios.get(`${API_BASE}/candidates`, { headers: authHeaders });
            setCandidates(response.data.data.candidates || []);
            setLoading(false);
        } catch (err: any) {
            console.error('Failed to load candidates:', err);
            setError(err.response?.data?.error || 'Failed to load candidates');
            setLoading(false);
        }
    };

    const loadTagsAndNotes = async () => {
        try {
            const authHeaders = await getAuthHeaders();
            // Load tags - key ONLY by session_id for unique matching per simulation run
            const tagsResponse = await axios.get(`${API_BASE}/candidates/tags`, { headers: authHeaders });
            if (tagsResponse.data.data) {
                const tagsMap: Record<string, string[]> = {};
                tagsResponse.data.data.forEach((tag: CandidateTag) => {
                    // Use session_id as the unique key for each simulation run
                    const key = tag.session_id;
                    if (key) {
                        if (!tagsMap[key]) {
                            tagsMap[key] = [];
                        }
                        if (!tagsMap[key].includes(tag.tag)) {
                            tagsMap[key].push(tag.tag);
                        }
                    }
                });
                setCandidateTags(tagsMap);
            }
        } catch (err) {
            console.error('Failed to load tags:', err);
        }

        try {
            const authHeaders = await getAuthHeaders();
            // Load notes - key ONLY by session_id for unique matching per simulation run
            const notesResponse = await axios.get(`${API_BASE}/candidates/notes`, { headers: authHeaders });
            if (notesResponse.data.data) {
                const notesMap: Record<string, CandidateNote[]> = {};
                notesResponse.data.data.forEach((note: CandidateNote) => {
                    // Use session_id as the unique key for each simulation run
                    const key = note.session_id;
                    if (key) {
                        if (!notesMap[key]) {
                            notesMap[key] = [];
                        }
                        // Avoid duplicates by checking note id
                        if (!notesMap[key].some(n => n.id === note.id)) {
                            notesMap[key].push(note);
                        }
                    }
                });
                setCandidateNotes(notesMap);
            }
        } catch (err) {
            console.error('Failed to load notes:', err);
        }
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    const filteredCandidates = useMemo(() => {
        return candidates
            .filter(candidate => {
                const matchesSearch = searchQuery === '' ||
                    candidate.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    (candidate.email && candidate.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
                    (candidate.simulation_title && candidate.simulation_title.toLowerCase().includes(searchQuery.toLowerCase()));

                const matchesStatus = statusFilter === 'all' || candidate.status === statusFilter;

                return matchesSearch && matchesStatus;
            })
            .sort((a, b) => {
                let aValue: any, bValue: any;

                switch (sortField) {
                    case 'name':
                        aValue = a.name;
                        bValue = b.name;
                        break;
                    case 'score':
                        aValue = a.average_score || 0;
                        bValue = b.average_score || 0;
                        break;
                    case 'date':
                        aValue = a.last_active || a.created_at || '';
                        bValue = b.last_active || b.created_at || '';
                        break;
                    case 'status':
                        aValue = a.status;
                        bValue = b.status;
                        break;
                    default:
                        return 0;
                }

                if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
                return 0;
            });
    }, [candidates, searchQuery, statusFilter, sortField, sortDirection]);

    const toggleSelection = (id: string) => {
        setSelectedCandidates(prev =>
            prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
        );
    };

    const selectAll = () => {
        if (selectedCandidates.length === filteredCandidates.length) {
            setSelectedCandidates([]);
        } else {
            setSelectedCandidates(filteredCandidates.map(c => c.id));
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const authHeaders = await getAuthHeaders();
            const params = selectedCandidates.length > 0 
                ? { ids: selectedCandidates.join(',') }
                : {};
            const response = await axios.get(`${API_BASE}/candidates/export`, {
                headers: authHeaders,
                params,
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `candidates_export_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to export candidates:', err);
            setError('Failed to export candidates');
        } finally {
            setExporting(false);
        }
    };

    const handleCompare = () => {
        if (selectedCandidates.length >= 2) {
            navigate(`/app/comparison?ids=${selectedCandidates.join(',')}`);
        }
    };

    const addTag = async (candidateId: string, tag: string) => {
        // Find the candidate to get user_id, session_id, and name
        const candidate = candidates.find(c => c.id === candidateId);
        const userId = candidate?.user_id || candidateId;
        const sessionId = candidate?.session_id || candidate?.id || candidateId;
        const candidateName = candidate?.name || 'Unknown';
        
        try {
            const authHeaders = await getAuthHeaders();
            // Use user_id as candidate_id in URL, pass session_id and name in body
            await axios.post(`${API_BASE}/candidates/${userId}/tags`, { 
                tag,
                session_id: sessionId,
                candidate_name: candidateName
            }, { headers: authHeaders });
            // Key by session_id for display
            setCandidateTags(prev => ({
                ...prev,
                [sessionId]: [...(prev[sessionId] || []), tag]
            }));
        } catch (err) {
            console.error('Failed to add tag:', err);
            // Fallback to local state only
            setCandidateTags(prev => ({
                ...prev,
                [sessionId]: [...(prev[sessionId] || []), tag]
            }));
        }
    };

    const removeTag = async (candidateId: string, tagToRemove: string) => {
        // Find the candidate to get user_id and session_id
        const candidate = candidates.find(c => c.id === candidateId);
        const userId = candidate?.user_id || candidateId;
        const sessionId = candidate?.session_id || candidate?.id || candidateId;
        
        try {
            const authHeaders = await getAuthHeaders();
            // Use user_id as candidate_id in URL, pass session_id as query param
            await axios.delete(`${API_BASE}/candidates/${userId}/tags/${encodeURIComponent(tagToRemove)}?session_id=${sessionId}`, { headers: authHeaders });
            // Key by session_id for display
            setCandidateTags(prev => ({
                ...prev,
                [sessionId]: (prev[sessionId] || []).filter(t => t !== tagToRemove)
            }));
        } catch (err) {
            console.error('Failed to remove tag:', err);
            // Fallback to local state only
            setCandidateTags(prev => ({
                ...prev,
                [sessionId]: (prev[sessionId] || []).filter(t => t !== tagToRemove)
            }));
        }
    };

    const handleAddAnnotation = (candidate: Candidate) => {
        setAnnotatingCandidate(candidate);
        setShowAnnotationModal(true);
    };

    const saveAnnotation = async () => {
        if (annotatingCandidate && annotationText.trim()) {
            // Get user_id, session_id, and name from the candidate
            const userId = annotatingCandidate.user_id || annotatingCandidate.id;
            const sessionId = annotatingCandidate.session_id || annotatingCandidate.id;
            const candidateName = annotatingCandidate.name || 'Unknown';
            
            try {
                const authHeaders = await getAuthHeaders();
                // Use user_id as candidate_id in URL, pass session_id and name in body
                const response = await axios.post(
                    `${API_BASE}/candidates/${userId}/notes`,
                    { 
                        content: annotationText,
                        session_id: sessionId,
                        candidate_name: candidateName
                    },
                    { headers: authHeaders }
                );
                const newNote: CandidateNote = response.data.data || {
                    id: Date.now().toString(),
                    candidate_id: userId,
                    session_id: sessionId,
                    content: annotationText,
                    created_by: 'current_user',
                    created_at: new Date().toISOString()
                };
                // Key by session_id for display
                setCandidateNotes(prev => ({
                    ...prev,
                    [sessionId]: [...(prev[sessionId] || []), newNote]
                }));
            } catch (err) {
                console.error('Failed to save note:', err);
                // Fallback to local state only
                const newNote: CandidateNote = {
                    id: Date.now().toString(),
                    candidate_id: userId,
                    session_id: sessionId,
                    content: annotationText,
                    created_by: 'current_user',
                    created_at: new Date().toISOString()
                };
                setCandidateNotes(prev => ({
                    ...prev,
                    [sessionId]: [...(prev[sessionId] || []), newNote]
                }));
            }
            setAnnotationText('');
            setShowAnnotationModal(false);
            setAnnotatingCandidate(null);
        }
    };

    const viewEvaluation = (candidate: Candidate) => {
        navigate(`/app/evaluation/${candidate.id}`);
    };

    // Helper to get tags for a candidate using session_id as key
    const getTagsForCandidate = (candidate: Candidate): string[] => {
        // candidate.id is the session_id (unique per simulation run)
        const sessionId = candidate.session_id || candidate.id;
        return candidateTags[sessionId] || [];
    };

    // Helper to get notes for a candidate using session_id as key
    const getNotesForCandidate = (candidate: Candidate): CandidateNote[] => {
        // candidate.id is the session_id (unique per simulation run)
        const sessionId = candidate.session_id || candidate.id;
        return candidateNotes[sessionId] || [];
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'bg-green-100 text-green-700 border-green-200';
            case 'in_progress': return 'bg-blue-100 text-blue-700 border-blue-200';
            case 'scheduled': return 'bg-purple-100 text-purple-700 border-purple-200';
            case 'not_started': return 'bg-gray-100 text-gray-700 border-gray-200';
            default: return 'bg-gray-100 text-gray-700 border-gray-200';
        }
    };

    const getScoreColor = (score: number) => {
        if (score >= 90) return 'text-green-600 font-bold';
        if (score >= 80) return 'text-emerald-600 font-semibold';
        if (score >= 70) return 'text-yellow-600 font-semibold';
        if (score >= 60) return 'text-orange-600';
        return 'text-red-600';
    };

    const getInitials = (name: string) => {
        const parts = name.split(' ').filter(Boolean);
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    };

    const getAvatarColor = (name: string) => {
        const colors = [
            'bg-blue-500', 'bg-emerald-500', 'bg-purple-500',
            'bg-orange-500', 'bg-pink-500', 'bg-cyan-500'
        ];
        const index = name.charCodeAt(0) % colors.length;
        return colors[index];
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-50">
                <AppHeader activePage="results" />
                <div className="flex items-center justify-center h-[calc(100vh-64px)]">
                    <div className="text-center">
                        <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                        <p className="text-slate-600">Loading results...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <AppHeader activePage="results" />

            <main className="max-w-7xl mx-auto px-6 py-8">
                <div className="space-y-6">
                    {/* Page Header */}
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-900 mb-2">Simulation Results</h1>
                            <p className="text-slate-600">
                                View and manage all candidate evaluations
                            </p>
                        </div>
                        <div className="flex gap-2">
                            {selectedCandidates.length > 0 && (
                                <>
                                    <button
                                        onClick={handleExport}
                                        disabled={exporting}
                                        className="flex items-center gap-2 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
                                    >
                                        <Download className="w-4 h-4" />
                                        Export ({selectedCandidates.length})
                                    </button>
                                    {selectedCandidates.length >= 2 && selectedCandidates.length <= 4 && (
                                        <button
                                            onClick={handleCompare}
                                            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition-colors"
                                        >
                                            Compare Selected
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                            {error}
                        </div>
                    )}

                    {/* Search and Filters */}
                    <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-4">
                        <div className="flex flex-col lg:flex-row gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search by candidate name or simulation..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                                />
                            </div>

                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white"
                            >
                                <option value="all">All Statuses</option>
                                <option value="completed">Completed</option>
                                <option value="in_progress">In Progress</option>
                                <option value="scheduled">Scheduled</option>
                                <option value="not_started">Not Started</option>
                            </select>
                        </div>

                        <div className="flex items-center justify-between text-sm text-slate-600">
                            <div>
                                Showing {filteredCandidates.length} of {candidates.length} candidates
                                {selectedCandidates.length > 0 && ` - ${selectedCandidates.length} selected`}
                            </div>
                            <button
                                onClick={selectAll}
                                className="text-emerald-600 hover:text-emerald-700 font-medium"
                            >
                                {selectedCandidates.length === filteredCandidates.length ? 'Deselect All' : 'Select All'}
                            </button>
                        </div>
                    </div>

                    {/* Results Table */}
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr>
                                        <th className="p-4 text-left">
                                            <input
                                                type="checkbox"
                                                checked={selectedCandidates.length === filteredCandidates.length && filteredCandidates.length > 0}
                                                onChange={selectAll}
                                                className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                                            />
                                        </th>
                                        <th className="p-4 text-left">
                                            <button
                                                onClick={() => handleSort('name')}
                                                className="flex items-center gap-2 font-semibold text-slate-900 hover:text-emerald-600 transition-colors"
                                            >
                                                Candidate
                                                <ArrowUpDown className="w-4 h-4" />
                                            </button>
                                        </th>
                                        <th className="p-4 text-left font-semibold text-slate-900">Simulation</th>
                                        <th className="p-4 text-center">
                                            <button
                                                onClick={() => handleSort('score')}
                                                className="flex items-center gap-2 font-semibold text-slate-900 hover:text-emerald-600 transition-colors mx-auto"
                                            >
                                                Score
                                                <ArrowUpDown className="w-4 h-4" />
                                            </button>
                                        </th>
                                        <th className="p-4 text-center">
                                            <button
                                                onClick={() => handleSort('status')}
                                                className="flex items-center gap-2 font-semibold text-slate-900 hover:text-emerald-600 transition-colors mx-auto"
                                            >
                                                Status
                                                <ArrowUpDown className="w-4 h-4" />
                                            </button>
                                        </th>
                                        <th className="p-4 text-center">
                                            <button
                                                onClick={() => handleSort('date')}
                                                className="flex items-center gap-2 font-semibold text-slate-900 hover:text-emerald-600 transition-colors mx-auto"
                                            >
                                                Date
                                                <ArrowUpDown className="w-4 h-4" />
                                            </button>
                                        </th>
                                        <th className="p-4 text-left font-semibold text-slate-900">Tags & Notes</th>
                                        <th className="p-4 text-center font-semibold text-slate-900">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                    {loading ? (
                                        <>
                                            <TableRowSkeleton />
                                            <TableRowSkeleton />
                                            <TableRowSkeleton />
                                            <TableRowSkeleton />
                                            <TableRowSkeleton />
                                        </>
                                    ) : filteredCandidates.length === 0 ? (
                                        <tr>
                                            <td colSpan={8} className="px-6 py-12 text-center">
                                                <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                                <h3 className="text-lg font-semibold text-slate-900 mb-2">No results found</h3>
                                                <p className="text-slate-600">
                                                    {searchQuery || statusFilter !== 'all'
                                                        ? 'Try adjusting your filters'
                                                        : 'Results will appear here after candidates complete simulations'}
                                                </p>
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredCandidates.map((candidate) => (
                                            <motion.tr
                                                key={candidate.id}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                className="hover:bg-slate-50 transition-colors"
                                            >
                                                <td className="p-4">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCandidates.includes(candidate.id)}
                                                        onChange={() => toggleSelection(candidate.id)}
                                                        className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                                                    />
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex items-center gap-3">
                                                        {candidate.image_url ? (
                                                            <img
                                                                src={candidate.image_url}
                                                                alt={candidate.name}
                                                                className="w-10 h-10 rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className={clsx(
                                                                'w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm',
                                                                getAvatarColor(candidate.name)
                                                            )}>
                                                                {getInitials(candidate.name)}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div className="font-medium text-slate-900">{candidate.name}</div>
                                                            <div className="text-sm text-slate-500">{candidate.position_applied || candidate.email || '-'}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="text-sm text-slate-900">{candidate.simulation_title || 'Simulation'}</div>
                                                </td>
                                                <td className="p-4 text-center">
                                                    {candidate.status === 'completed' && candidate.average_score !== null ? (
                                                        <span className={clsx('text-lg font-semibold', getScoreColor(candidate.average_score))}>
                                                            {candidate.average_score.toFixed(0)}
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-400">-</span>
                                                    )}
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex justify-center">
                                                        <span className={clsx('px-3 py-1 rounded-full text-xs font-medium border', getStatusColor(candidate.status))}>
                                                            {candidate.status.replace('_', ' ')}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="p-4 text-center text-sm text-slate-600">
                                                    {formatDate(candidate.last_active || candidate.created_at)}
                                                </td>
                                                <td 
                                                    className="p-4"
                                                    onMouseEnter={(e) => {
                                                        if (tooltipHideTimeoutRef.current) {
                                                            clearTimeout(tooltipHideTimeoutRef.current);
                                                            tooltipHideTimeoutRef.current = null;
                                                        }
                                                        const tags = getTagsForCandidate(candidate);
                                                        const notes = getNotesForCandidate(candidate);
                                                        if (tags.length > 0 || notes.length > 0) {
                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                            setTooltipPosition({ top: rect.top, left: rect.left + rect.width / 2 });
                                                            setHoveredCellCandidate(candidate.id);
                                                        }
                                                    }}
                                                    onMouseLeave={() => {
                                                        tooltipHideTimeoutRef.current = setTimeout(() => {
                                                            setHoveredCellCandidate(null);
                                                            setTooltipPosition(null);
                                                        }, 150);
                                                    }}
                                                >
                                                    <div className="space-y-2">
                                                        {/* Tags - show max 2 */}
                                                        {(() => {
                                                            const tags = getTagsForCandidate(candidate);
                                                            return tags.length > 0 && (
                                                            <div className="flex flex-wrap gap-1 items-center">
                                                                {tags.slice(0, 2).map((tag, index) => (
                                                                    <span
                                                                        key={index}
                                                                        className={clsx(
                                                                            'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
                                                                            TAG_COLORS[tag] || TAG_COLORS['default']
                                                                        )}
                                                                    >
                                                                        {tag}
                                                                        <button
                                                                            onClick={() => removeTag(candidate.id, tag)}
                                                                            className="hover:opacity-70"
                                                                        >
                                                                            <X className="w-3 h-3" />
                                                                        </button>
                                                                    </span>
                                                                ))}
                                                                {tags.length > 2 && (
                                                                    <span className="text-xs text-slate-500 font-medium cursor-help">
                                                                        +{tags.length - 2} more
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )})()}
                                                        {/* Notes - show max 2 chips */}
                                                        {(() => {
                                                            const notes = getNotesForCandidate(candidate);
                                                            return notes.length > 0 && (
                                                            <div className="flex flex-wrap gap-1 items-center">
                                                                {notes.slice(0, 2).map((note, index) => (
                                                                    <span
                                                                        key={note.id || index}
                                                                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded text-xs font-medium max-w-[120px]"
                                                                    >
                                                                        <StickyNote className="w-3 h-3 flex-shrink-0" />
                                                                        <span className="truncate">{note.content.slice(0, 15)}{note.content.length > 15 ? '...' : ''}</span>
                                                                    </span>
                                                                ))}
                                                                {notes.length > 2 && (
                                                                    <span className="text-xs text-slate-500 font-medium cursor-help">
                                                                        +{notes.length - 2} more
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )})()}
                                                        <div className="flex gap-1">
                                                            <button
                                                                onClick={() => handleAddAnnotation(candidate)}
                                                                className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                                                            >
                                                                + Add note
                                                            </button>
                                                            <span className="text-slate-300">|</span>
                                                            <button
                                                                onClick={(e) => {
                                                                    if (showTagMenu === candidate.id) {
                                                                        setShowTagMenu(null);
                                                                        setTagMenuPosition(null);
                                                                    } else {
                                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                                        setTagMenuPosition({
                                                                            top: rect.top - 8,
                                                                            left: rect.left
                                                                        });
                                                                        setShowTagMenu(candidate.id);
                                                                    }
                                                                }}
                                                                className="text-xs text-emerald-600 hover:text-emerald-700 font-medium"
                                                            >
                                                                + Add tag
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex justify-center gap-2">
                                                        {candidate.status === 'completed' && (
                                                            <button
                                                                onClick={() => viewEvaluation(candidate)}
                                                                className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                                                                title="View Evaluation"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </main>

            {/* Fixed Tag Dropdown Menu */}
            {showTagMenu && tagMenuPosition && (
                <>
                    <div 
                        className="fixed inset-0 z-[100]" 
                        onClick={() => {
                            setShowTagMenu(null);
                            setTagMenuPosition(null);
                        }}
                    />
                    <div 
                        className="fixed z-[101] w-48 bg-white rounded-lg shadow-xl border border-slate-200 py-1"
                        style={{
                            top: tagMenuPosition.top,
                            left: tagMenuPosition.left,
                            transform: 'translateY(-100%)'
                        }}
                    >
                        <div className="max-h-48 overflow-y-auto">
                            {PREDEFINED_TAGS.filter(tag => !(candidateTags[showTagMenu] || []).includes(tag)).map(tag => (
                                <button
                                    key={tag}
                                    onClick={() => {
                                        addTag(showTagMenu, tag);
                                        setShowTagMenu(null);
                                        setTagMenuPosition(null);
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors"
                                >
                                    {tag}
                                </button>
                            ))}
                            {PREDEFINED_TAGS.filter(tag => !(candidateTags[showTagMenu] || []).includes(tag)).length === 0 && (
                                <div className="px-3 py-2 text-sm text-slate-400">All tags applied</div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Fixed Hover Tooltip for Tags & Notes */}
            {hoveredCellCandidate && tooltipPosition && (
                <div 
                    className="fixed z-[200] w-80 bg-slate-900 rounded-xl shadow-2xl border border-slate-700 p-4"
                    style={{
                        top: tooltipPosition.top,
                        left: tooltipPosition.left,
                        transform: 'translate(-50%, -100%) translateY(-8px)'
                    }}
                    onMouseEnter={() => {
                        if (tooltipHideTimeoutRef.current) {
                            clearTimeout(tooltipHideTimeoutRef.current);
                            tooltipHideTimeoutRef.current = null;
                        }
                    }}
                    onMouseLeave={() => {
                        setHoveredCellCandidate(null);
                        setTooltipPosition(null);
                    }}
                >
                    {/* Tooltip Arrow */}
                    <div className="absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-full">
                        <div className="w-3 h-3 bg-slate-900 border-r border-b border-slate-700 transform rotate-45 -translate-y-1.5"></div>
                    </div>
                    
                    {/* All Tags */}
                    {(() => {
                        const hoveredCandidate = candidates.find(c => c.id === hoveredCellCandidate);
                        const tags = hoveredCandidate ? getTagsForCandidate(hoveredCandidate) : [];
                        return tags.length > 0 && (
                        <div className="mb-3">
                            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Tags</div>
                            <div className="flex flex-wrap gap-1.5">
                                {tags.map((tag, idx) => (
                                    <span 
                                        key={idx} 
                                        className={clsx('inline-flex items-center px-2 py-1 rounded text-xs font-medium', TAG_COLORS[tag] || TAG_COLORS['default'])}
                                    >
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )})()}
                    
                    {/* All Notes */}
                    {(() => {
                        const hoveredCandidate = candidates.find(c => c.id === hoveredCellCandidate);
                        const notes = hoveredCandidate ? getNotesForCandidate(hoveredCandidate) : [];
                        return notes.length > 0 && (
                        <div>
                            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Notes</div>
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                                {notes.map((note, idx) => (
                                    <div key={note.id || idx} className="bg-slate-800/50 rounded-lg p-2.5 border border-slate-700/50">
                                        <p className="text-sm text-slate-200 leading-relaxed">{note.content}</p>
                                        {note.created_at && (
                                            <p className="text-xs text-slate-500 mt-1.5">
                                                {new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )})()}
                    
                    {/* Empty state */}
                    {(() => {
                        const hoveredCandidate = candidates.find(c => c.id === hoveredCellCandidate);
                        const tags = hoveredCandidate ? getTagsForCandidate(hoveredCandidate) : [];
                        const notes = hoveredCandidate ? getNotesForCandidate(hoveredCandidate) : [];
                        return tags.length === 0 && notes.length === 0 && (
                        <p className="text-sm text-slate-400 text-center py-2">No tags or notes</p>
                    )})()}
                </div>
            )}

            {/* Annotation Modal */}
            <AnimatePresence>
                {showAnnotationModal && annotatingCandidate && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold text-slate-900">Add Note</h3>
                                <button
                                    onClick={() => {
                                        setShowAnnotationModal(false);
                                        setAnnotatingCandidate(null);
                                        setAnnotationText('');
                                    }}
                                    className="p-1 hover:bg-slate-100 rounded transition-colors"
                                >
                                    <X className="w-5 h-5 text-slate-600" />
                                </button>
                            </div>

                            <div className="mb-4">
                                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                    {annotatingCandidate.image_url ? (
                                        <img
                                            src={annotatingCandidate.image_url}
                                            alt={annotatingCandidate.name}
                                            className="w-10 h-10 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className={clsx(
                                            'w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm',
                                            getAvatarColor(annotatingCandidate.name)
                                        )}>
                                            {getInitials(annotatingCandidate.name)}
                                        </div>
                                    )}
                                    <div>
                                        <div className="font-medium text-slate-900">{annotatingCandidate.name}</div>
                                        <div className="text-sm text-slate-600">{annotatingCandidate.simulation_title || 'Simulation'}</div>
                                    </div>
                                </div>
                            </div>

                            <textarea
                                value={annotationText}
                                onChange={(e) => setAnnotationText(e.target.value)}
                                placeholder="Add your notes or observations about this candidate..."
                                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
                                rows={4}
                            />

                            <div className="flex gap-3 mt-4">
                                <button
                                    onClick={() => {
                                        setShowAnnotationModal(false);
                                        setAnnotatingCandidate(null);
                                        setAnnotationText('');
                                    }}
                                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={saveAnnotation}
                                    disabled={!annotationText.trim()}
                                    className="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
                                >
                                    Save Note
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}

